import { DocumentTextIcon, RectangleStackIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

interface CreateMatterFlowDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog to select between Quick Create (old flow) and Guided Setup (new flow)
 * for creating a new matter.
 */
export const CreateMatterFlowDialog: React.FC<CreateMatterFlowDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const navigate = useNavigate();

  const handleSelectFlow = (flow: 'quick' | 'guided') => {
    onOpenChange(false);
    navigate(flow === 'quick' ? '/matters/create' : '/matters/ingestion');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create New Matter</DialogTitle>
          <DialogDescription>
            Choose how you&apos;d like to create your matter
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 pt-2">
          {/* Quick Create Card */}
          <button
            type="button"
            onClick={() => handleSelectFlow('quick')}
            className={cn(
              'flex flex-col items-center rounded-lg border border-zinc-200 p-4 text-center',
              'transition-colors hover:border-primary hover:bg-primary/5',
              'dark:border-zinc-700 dark:hover:border-primary dark:hover:bg-primary/10',
            )}
          >
            <DocumentTextIcon className="mb-2 h-8 w-8 text-zinc-400" />
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
              Quick Create
            </h3>
            <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
              Simple form — enter basics and go
            </p>
          </button>

          {/* Guided Setup Card */}
          <button
            type="button"
            onClick={() => handleSelectFlow('guided')}
            className={cn(
              'flex flex-col items-center rounded-lg border border-zinc-200 p-4 text-center',
              'transition-colors hover:border-primary hover:bg-primary/5',
              'dark:border-zinc-700 dark:hover:border-primary dark:hover:bg-primary/10',
            )}
          >
            <RectangleStackIcon className="mb-2 h-8 w-8 text-primary" />
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
              Guided Setup
            </h3>
            <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
              Step-by-step wizard with case ingestion
            </p>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
